import createFetchClient, {type Middleware} from 'openapi-fetch'
import createReactQueryClient from 'openapi-react-query'
import type {paths} from './api'

const AUTH_LS_KEY = 'kt-auth-react-v'

/**
 * Access token kept in memory and injected into every request by the auth
 * middleware below. The auth layer (AuthProvider) is the single source of
 * truth — it calls `setAuthToken` on login, refresh, init and logout.
 */
let accessToken: string | undefined

export const setAuthToken = (token: string | undefined) => {
  accessToken = token
}

/**
 * Current in-memory access token. Use for imperative requests that bypass the
 * openapi-fetch middleware (e.g. XHR uploads that need progress events).
 */
export const getAuthToken = (): string | undefined => accessToken

/* ── Token refresh ─────────────────────────────────────────────────────── */

let refreshPromise: Promise<string | null> | null = null

const doRefresh = async (): Promise<string | null> => {
  try {
    const lsValue = localStorage.getItem(AUTH_LS_KEY)
    if (!lsValue) return null
    const auth = JSON.parse(lsValue)
    if (!auth?.refresh) return null

    const res = await fetch(`${process.env.REACT_APP_API_URL}/wms/auth/token/refresh/`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({refresh: auth.refresh}),
    })
    if (!res.ok) return null

    const data = await res.json()
    if (!data.access) return null

    // Persist new access token
    const updated = {...auth, access: data.access}
    localStorage.setItem(AUTH_LS_KEY, JSON.stringify(updated))
    accessToken = data.access
    return data.access
  } catch {
    return null
  }
}

/* ── Middleware ─────────────────────────────────────────────────────────── */

// Store request clones so we can retry after a successful token refresh.
const clones = new WeakMap<Request, Request>()

const authMiddleware: Middleware = {
  onRequest({request}) {
    if (accessToken) {
      request.headers.set('Authorization', `Bearer ${accessToken}`)
    }
    // Clone BEFORE the body is consumed by fetch.
    clones.set(request, request.clone())
    return request
  },

  async onResponse({request, response}) {
    if (response.status !== 401) return response

    // Never retry the refresh endpoint itself (avoid infinite loop).
    if (request.url.includes('/auth/token/refresh')) return response

    const clone = clones.get(request)
    clones.delete(request)

    // Single refresh at a time — concurrent 401s share one refresh call.
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null
      })
    }
    const newToken = await refreshPromise

    if (!newToken) {
      // Refresh failed — clear auth and redirect to login.
      localStorage.removeItem(AUTH_LS_KEY)
      accessToken = undefined
      window.location.href = '/auth'
      return response
    }

    if (!clone) return response

    // Retry the original request with the new token.
    clone.headers.set('Authorization', `Bearer ${newToken}`)
    return fetch(clone)
  },
}

/* ── Clients ───────────────────────────────────────────────────────────── */

/**
 * Low-level typed fetch client (openapi-fetch).
 * Use it for imperative calls outside of React, e.g. loaders or event handlers:
 *   const {data, error} = await fetchClient.GET('/some/path')
 */
export const fetchClient = createFetchClient<paths>({
  baseUrl: process.env.REACT_APP_API_URL,
})

fetchClient.use(authMiddleware)

/**
 * TanStack Query bindings (openapi-react-query).
 * Use it inside components:
 *   const {data} = $api.useQuery('get', '/some/path')
 *   const mutation = $api.useMutation('post', '/some/path')
 */
export const $api = createReactQueryClient(fetchClient)
